import { Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent, HttpResponse } from '@angular/common/http';
import { Observable, Subject, throwError } from 'rxjs';
import { tap, filter, take, switchMap, catchError } from 'rxjs/operators';

/**
 * Complete CSRF protection interceptor for BFF (Backend for Frontend) pattern.
 * 
 * This interceptor handles both:
 * 1. Extracting CSRF token from response headers (X-GATEWAY-XSRF-TOKEN)
 * 2. Adding CSRF token to outgoing request headers for state-changing operations
 * 3. CRITICAL: Waiting for token to be available before sending POST/PUT/DELETE/PATCH requests
 * 
 * WHY: On localhost (different ports) and production (different subdomains),
 * JavaScript cannot read cookies via document.cookie due to same-origin policy.
 * Backend sends token in response header, and we send it back in request header.
 * 
 * IMPORTANT FOR SHOP: This works for both authenticated AND unauthenticated users.
 * Anonymous users can add items to cart (POST requests) without being logged in,
 * and CSRF protection will still work because the token is extracted from response
 * headers on the first GET request (e.g., loading the shop homepage or /company/owner).
 * 
 * RACE CONDITION FIX: If a POST request (e.g., /pdp/banner from home component) 
 * arrives BEFORE the token is extracted from the first response, this interceptor 
 * will wait for the token to become available before sending the request.
 * 
 * providedIn: 'root' ensures a single instance is shared across the app.
 */
@Injectable({ providedIn: 'root' })
export class GatewayXsrfInterceptor implements HttpInterceptor {

    private token: string | null = null;
    private readonly TOKEN_HEADER_NAME = 'X-GATEWAY-XSRF-TOKEN';
    private readonly tokenSubject = new Subject<string>();
    private tokenInitialized = false;

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const method = req.method.toUpperCase();
        
        // GET, HEAD, OPTIONS don't need CSRF token - process immediately
        if (method === 'GET' || method === 'HEAD' || method === 'OPTIONS') {
            return this.handleRequest(req, next);
        }

        // POST/PUT/DELETE/PATCH need CSRF token
        // If token is available, use it immediately
        if (this.token) {
            const modifiedReq = req.clone({
                setHeaders: {
                    [this.TOKEN_HEADER_NAME]: this.token
                }
            });
            return this.handleRequest(modifiedReq, next);
        }

        // Token not yet available - wait for it
        // This handles the race condition where POST requests (e.g., /pdp/banner) 
        // arrive before the first GET response (e.g., /company/owner) sets the token
        console.warn(`CSRF token not yet available for ${method} ${req.url}. Waiting for token...`);
        
        return this.tokenSubject.pipe(
            filter(token => !!token),
            take(1),
            switchMap(token => {
                const modifiedReq = req.clone({
                    setHeaders: {
                        [this.TOKEN_HEADER_NAME]: token
                    }
                });
                console.log(`CSRF token received. Retrying ${method} ${req.url}`);
                return this.handleRequest(modifiedReq, next);
            }),
            catchError(error => {
                console.error(`Error waiting for CSRF token for ${method} ${req.url}:`, error);
                return throwError(error);
            })
        );
    }

    private handleRequest(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            tap(event => {
                if (event instanceof HttpResponse) {
                    const csrfToken = event.headers.get(this.TOKEN_HEADER_NAME);
                    if (csrfToken && csrfToken !== this.token) {
                        this.token = csrfToken;
                        
                        // Notify all waiting requests that token is available
                        if (!this.tokenInitialized) {
                            this.tokenInitialized = true;
                            console.log('CSRF token initialized:', csrfToken.substring(0, 8) + '...');
                        }
                        this.tokenSubject.next(csrfToken);
                    }
                }
            })
        );
    }
}

